import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLiveUpdates } from '../hooks/useLiveUpdates';

/*
 * Copyright (c) 2026 Notiosoft SA.
 * All rights reserved.
 *
 * Full-width flashing bar for the instant a new report is submitted —
 * distinct from NotificationBell, which is a "needs your attention"
 * worklist (new/unassigned + approaching/breached SLA) that a report
 * drops out of the moment it's auto-assigned. This banner is pure
 * awareness: every new report flashes it, assigned or not, so support
 * staff stay engaged with submission activity even when dispatch is
 * working exactly as intended and nothing actually needs them to act.
 */

interface NewReportEvent {
  id: string;
  reportNumber?: string;
  title: string;
  priority?: string;
  status?: string;
}

const AUTO_DISMISS_MS = 10000;

const NewReportBanner: React.FC = () => {
  const [report, setReport] = useState<NewReportEvent | null>(null);
  const navigate = useNavigate();
  const timerRef = React.useRef<number | undefined>(undefined);

  useLiveUpdates((message) => {
    if (message.type !== 'new_report' || !message.data) return;
    setReport(message.data as NewReportEvent);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setReport(null), AUTO_DISMISS_MS);
  });

  React.useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  if (!report) return null;

  const dismiss = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setReport(null);
  };

  // Dispatch runs synchronously before this event fires — a report that's
  // still 'submitted' means auto-assignment didn't find anyone, so nobody
  // is on it. That's a different signal from "a report came in and was
  // handled fine," so it gets the same red flash as an escalated fault
  // instead of the calm blue "just so you know" one.
  const unassigned = report.status === 'submitted';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`${unassigned ? 'animate-flash-critical' : 'animate-flash-new'} flex items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium text-white animate-slide-up`}
    >
      <BellAlertIcon className="h-4 w-4 shrink-0 animate-pulse" />
      <button
        type="button"
        onClick={() => {
          navigate(`/reports/${report.id}`);
          dismiss();
        }}
        className="underline decoration-white/50 underline-offset-2 hover:decoration-white"
      >
        {unassigned ? 'Unassigned report needs attention' : 'New report just submitted'}
        {report.reportNumber ? ` — ${report.reportNumber}` : ''}: {report.title}
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="ml-2 rounded p-0.5 hover:bg-white/20"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default NewReportBanner;
